import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { btn } from "../styles/shared";
import { calcLBM, getCurrentBodyComposition } from "../utils/calculations";
import { fmtDate, getTodayDate } from "../utils/formatting";

const WORKOUT_TYPES = [
  "running", "trail running", "hiking", "walking", "cycling",
  "freediving", "swimming",
  "gym - weights", "gym - hiit", "gym - cardio", "gym - yoga",
  "other",
];

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isSyncedWorkout(workout) {
  return String(workout?.sourceId || "").startsWith("health_connect_")
    || String(workout?.id || "").startsWith("hc-");
}

function timeRange(workout) {
  if (!workout.startTime || !workout.endTime) return "";
  const start = new Date(workout.startTime);
  const end = new Date(workout.endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  return `${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function formatSyncDate(value) {
  if (!value) return "Not synced yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export default function Fitness({ workouts = [], saveWorkouts, deleteFitnessWorkout, fitnessGoals, weightLog = [], healthConnect = {}, showToast }) {
  const [showManualForm, setShowManualForm] = useState(false);
  const emptyManual = { date: getTodayDate(), workoutType: "running", steps: "", caloriesBurned: "", heartRateAvg: "", heartRateMax: "", activeMinutes: "", distance: "", notes: "" };
  const [manualForm, setManualForm] = useState(emptyManual);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const composition = getCurrentBodyComposition(fitnessGoals, weightLog);
  const bodyFat = composition.bodyFat ?? fitnessGoals.bodyFatCurrent;
  const leanMass = composition.leanMass ?? calcLBM(composition.weight ?? fitnessGoals.weightCurrent, fitnessGoals.height);

  const manualWorkouts = workouts.filter(workout => !isSyncedWorkout(workout));
  const syncedWorkouts = workouts.filter(isSyncedWorkout);
  const allWorkouts = [...manualWorkouts, ...syncedWorkouts].sort((a, b) => a.date.localeCompare(b.date));
  const dailyActivity = Array.isArray(healthConnect.daily) ? healthConnect.daily : [];
  const dailyByDate = new Map(dailyActivity.map(day => [day.date, day]));

  async function saveManualEntry() {
    if (!manualForm.date) return showToast("Please enter a date");
    const entry = {
      id: Date.now(),
      date: manualForm.date,
      workoutType: manualForm.workoutType || "other",
      steps: number(manualForm.steps),
      caloriesBurned: number(manualForm.caloriesBurned),
      heartRateAvg: number(manualForm.heartRateAvg),
      heartRateMax: number(manualForm.heartRateMax),
      activeMinutes: number(manualForm.activeMinutes),
      distance: number(manualForm.distance),
      notes: manualForm.notes || "",
    };
    const ok = await saveWorkouts([...manualWorkouts, entry]);
    showToast(ok ? "Workout logged manually" : "Workout was not saved");
    if (ok) {
      setManualForm(emptyManual);
      setShowManualForm(false);
    }
  }

  function startEdit(workout) {
    if (isSyncedWorkout(workout)) return;
    setEditingId(workout.id);
    setEditForm({
      date: workout.date,
      workoutType: workout.workoutType || "other",
      steps: workout.steps || "",
      caloriesBurned: workout.caloriesBurned || "",
      heartRateAvg: workout.heartRateAvg || "",
      heartRateMax: workout.heartRateMax || "",
      activeMinutes: workout.activeMinutes || "",
      distance: workout.distance || "",
      notes: workout.notes || "",
    });
  }

  async function saveEdit() {
    const updated = manualWorkouts.map(workout => workout.id !== editingId ? workout : {
      ...workout,
      date: editForm.date,
      workoutType: editForm.workoutType || "other",
      steps: number(editForm.steps),
      caloriesBurned: number(editForm.caloriesBurned),
      heartRateAvg: number(editForm.heartRateAvg),
      heartRateMax: number(editForm.heartRateMax),
      activeMinutes: number(editForm.activeMinutes),
      distance: number(editForm.distance),
      notes: editForm.notes || "",
    });
    const ok = await saveWorkouts(updated);
    showToast(ok ? "Workout updated" : "Workout was not saved");
    if (ok) setEditingId(null);
  }

  async function deleteWorkout(workout) {
    const ok = isSyncedWorkout(workout)
      ? await deleteFitnessWorkout(workout)
      : await saveWorkouts(manualWorkouts.filter(item => item.id !== workout.id));
    showToast(ok ? "Workout deleted" : "Workout could not be deleted");
    if (ok && editingId === workout.id) setEditingId(null);
  }

  const activityChart = [...dailyActivity]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(day => ({
      date: fmtDate(day.date),
      steps: day.steps || 0,
      hr: day.heartRateAvg || 0,
      active: day.activeMinutes || 0,
      calories: day.calories || 0,
    }));

  const workoutChart = [...allWorkouts.reduce((byDate, workout) => {
    const current = byDate.get(workout.date) || {
      date: workout.date,
      calories: 0,
      hrTotal: 0,
      hrSamples: 0,
      active: 0,
      distance: 0,
    };
    const hr = number(workout.heartRateAvg);
    const hrSamples = hr > 0 ? number(workout.heartRateSamples) || 1 : 0;
    current.calories += number(workout.caloriesBurned);
    current.hrTotal += hr * hrSamples;
    current.hrSamples += hrSamples;
    current.active += number(workout.activeMinutes);
    current.distance += number(workout.distance);
    byDate.set(workout.date, current);
    return byDate;
  }, new Map()).values()].sort((a, b) => a.date.localeCompare(b.date)).map(day => ({
    date: fmtDate(day.date),
    calories: Math.round(day.calories),
    hr: day.hrSamples > 0 ? Math.round(day.hrTotal / day.hrSamples) : 0,
    active: Math.round(day.active),
    distance: Math.round(day.distance * 100) / 100,
  }));

  const stepDays = dailyActivity.filter(day => number(day.steps) > 0);
  const avgSteps = stepDays.length
    ? Math.round(stepDays.reduce((sum, day) => sum + number(day.steps), 0) / stepDays.length)
    : 0;
  const hrDays = dailyActivity.filter(day => number(day.heartRateAvg) > 0);
  const hrSampleTotal = hrDays.reduce((sum, day) => sum + (number(day.heartRateSamples) || 1), 0);
  const avgHR = hrDays.length
    ? Math.round(hrDays.reduce((sum, day) => sum + (number(day.heartRateAvg) * (number(day.heartRateSamples) || 1)), 0) / hrSampleTotal)
    : 0;

  const dates = [...new Set([
    ...dailyActivity.map(day => day.date),
    ...allWorkouts.map(workout => workout.date),
  ])].sort().reverse();

  const fieldStyle = { background: "rgba(0,0,0,0.4)", border: "1px solid rgba(52,211,153,0.2)", borderRadius: 7, padding: "8px 10px", color: "#fff", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
  const fieldStyleSm = { ...fieldStyle, padding: "7px 9px", fontSize: 12 };
  const labelStyle = { fontSize: 9, fontFamily: "'Space Mono',monospace", color: "#8dddb5", textTransform: "uppercase", letterSpacing: 1 };
  const panelStyle = { background: "linear-gradient(135deg,#061a14,#041108)", border: "1px solid rgba(52,211,153,0.15)", borderRadius: 16 };
  const workoutFields = [
    ["date", "Date", "date", null],
    ["workoutType", "Type", "select", WORKOUT_TYPES],
    ["caloriesBurned", "Calories", "number", null],
    ["activeMinutes", "Active min", "number", null],
    ["steps", "Steps", "number", null],
    ["distance", "Distance (km)", "number", null],
    ["heartRateAvg", "Avg HR", "number", null],
    ["heartRateMax", "Max HR", "number", null],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, letterSpacing: -0.5 }}>Fitness</h1>
        <p style={{ color: "#999", margin: "4px 0 0", fontSize: 14 }}>Daily watch activity, synced workouts, and manual training logs</p>
      </div>

      <div style={{ ...panelStyle, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: "'Space Mono',monospace", color: "#8dddb5", textTransform: "uppercase", letterSpacing: 1 }}>Watch Sync</div>
            <div style={{ fontSize: 13, color: "#e2e8f0", marginTop: 4 }}>Last sync: {formatSyncDate(healthConnect.lastSyncedAt)}</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#34d399", background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.16)", borderRadius: 7, padding: "6px 9px" }}>{healthConnect.steps || 0} step days</span>
            <span style={{ fontSize: 12, color: "#f87171", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.16)", borderRadius: 7, padding: "6px 9px" }}>{healthConnect.heartRate || 0} HR days</span>
            <span style={{ fontSize: 12, color: "#fb923c", background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.16)", borderRadius: 7, padding: "6px 9px" }}>{healthConnect.workouts || 0} workouts</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button onClick={() => setShowManualForm(value => !value)}
          style={{ ...btn, padding: "10px 18px", fontSize: 13, background: showManualForm ? "rgba(52,211,153,0.2)" : "rgba(52,211,153,0.1)", color: showManualForm ? "#34d399" : "#22c55e", border: `1px solid ${showManualForm ? "rgba(52,211,153,0.4)" : "rgba(52,211,153,0.2)"}` }}>
          {showManualForm ? "Close Form" : "+ Log Workout"}
        </button>
      </div>

      {showManualForm && (
        <div style={{ background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.25)", borderRadius: 13, padding: "16px 20px" }}>
          <div style={{ fontSize: 12, fontFamily: "'Space Mono',monospace", color: "#34d399", marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Manual Workout Entry</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 10, marginBottom: 14 }}>
            {workoutFields.map(([key, label, type, options]) => (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={labelStyle}>{label}</label>
                {type === "select" ? (
                  <select value={manualForm[key]} onChange={event => setManualForm(form => ({ ...form, [key]: event.target.value }))} style={fieldStyle}>
                    {options.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                ) : (
                  <input type={type} value={manualForm[key]} onChange={event => setManualForm(form => ({ ...form, [key]: event.target.value }))} placeholder={type === "number" ? "0" : ""} style={fieldStyle} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 }}>
            <label style={labelStyle}>Notes</label>
            <input type="text" value={manualForm.notes} onChange={event => setManualForm(form => ({ ...form, notes: event.target.value }))} placeholder="Optional notes" style={fieldStyle} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveManualEntry} style={{ ...btn, padding: "9px 20px", fontSize: 13, background: "rgba(52,211,153,0.2)", color: "#34d399", border: "1px solid rgba(52,211,153,0.4)" }}>Save Workout</button>
            <button onClick={() => { setShowManualForm(false); setManualForm(emptyManual); }} style={{ ...btn, padding: "9px 14px", fontSize: 13, background: "rgba(255,255,255,0.05)", color: "#777" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 10 }}>
        {[
          ["Workouts", `${allWorkouts.length} logged`],
          ["Avg Steps", avgSteps.toLocaleString()],
          ["Avg Heart Rate", avgHR > 0 ? `${avgHR}bpm` : "-"],
          ["Synced Days", dailyActivity.length],
          ["BF% Target", `${fitnessGoals.bodyFatTarget || 10}%`],
          ["Lean Body Mass", leanMass != null ? `${leanMass.toFixed(1)}kg` : "-"],
        ].map(([label, value]) => (
          <div key={label} style={{ ...panelStyle, borderRadius: 12, padding: "13px 15px" }}>
            <div style={{ fontSize: 10, fontFamily: "'Space Mono',monospace", color: "#8dddb5", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Space Mono',monospace", color: "#fff" }}>{value}</div>
          </div>
        ))}
      </div>

      {activityChart.length > 1 && (
        <div style={{ ...panelStyle, padding: 20 }}>
          <div style={{ fontSize: 12, fontFamily: "'Space Mono',monospace", color: "#8dddb5", marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Daily Watch Activity</div>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={activityChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(52,211,153,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#8dddb5", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#8dddb5", fontSize: 10 }} axisLine={false} tickLine={false} width={45} />
              <Tooltip contentStyle={{ background: "#061a14", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 10, color: "#e2e8f0" }} labelStyle={{ color: "#34d399", fontSize: 11 }} />
              <Line type="monotone" dataKey="steps" stroke="#34d399" strokeWidth={2.5} dot={{ r: 3, fill: "#34d399", strokeWidth: 0 }} name="Steps" />
              <Line type="monotone" dataKey="hr" stroke="#f87171" strokeWidth={2} dot={false} name="Avg HR" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {workoutChart.length > 1 && (
        <div style={{ ...panelStyle, padding: 20 }}>
          <div style={{ fontSize: 12, fontFamily: "'Space Mono',monospace", color: "#8dddb5", marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Workout Load</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={workoutChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(52,211,153,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#8dddb5", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#8dddb5", fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={{ background: "#061a14", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 10, color: "#e2e8f0" }} labelStyle={{ color: "#34d399", fontSize: 11 }} />
              <Line type="monotone" dataKey="calories" stroke="#fb923c" strokeWidth={2} dot={false} name="Calories" />
              <Line type="monotone" dataKey="active" stroke="#818cf8" strokeWidth={2} dot={false} name="Active min" />
              <Line type="monotone" dataKey="distance" stroke="#34d399" strokeWidth={2} dot={false} name="Distance" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {dates.length === 0 && (
        <div style={{ ...panelStyle, padding: 40, textAlign: "center", color: "#8dddb5", fontSize: 13 }}>
          Sync your watch or log a workout manually to start your fitness timeline.
        </div>
      )}

      {dates.length > 0 && (
        <div style={{ ...panelStyle, overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", borderBottom: "1px solid rgba(52,211,153,0.1)", fontSize: 11, fontFamily: "'Space Mono',monospace", color: "#8dddb5", textTransform: "uppercase", letterSpacing: 1 }}>
            Activity Timeline
          </div>
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            {dates.map(date => {
              const day = dailyByDate.get(date);
              const dayWorkouts = allWorkouts.filter(workout => workout.date === date).sort((a, b) => String(a.startTime || "").localeCompare(String(b.startTime || "")));
              return (
                <div key={date} style={{ borderBottom: "1px solid rgba(52,211,153,0.08)", padding: "14px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontFamily: "'Space Mono',monospace", color: "#e2e8f0" }}>{date}</div>
                      {day && <div style={{ fontSize: 11, color: "#6ec89a", marginTop: 3 }}>Daily summary from {day.source || "Health Connect"}</div>}
                    </div>
                    {day && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {number(day.steps) > 0 && <span style={{ fontSize: 11, color: "#22d3ee" }}>{number(day.steps).toLocaleString()} steps</span>}
                        {number(day.heartRateAvg) > 0 && <span style={{ fontSize: 11, color: "#f87171" }}>{day.heartRateAvg}bpm avg</span>}
                        {number(day.activeMinutes) > 0 && <span style={{ fontSize: 11, color: "#818cf8" }}>{day.activeMinutes}min active</span>}
                        {number(day.distance) > 0 && <span style={{ fontSize: 11, color: "#34d399" }}>{day.distance}km</span>}
                      </div>
                    )}
                  </div>

                  {dayWorkouts.map((workout, index) => (
                    <div key={workout.id || `${date}-${index}`} style={{ background: "rgba(0,0,0,0.18)", border: "1px solid rgba(52,211,153,0.08)", borderRadius: 8, padding: 12, marginTop: 8 }}>
                      {editingId === workout.id ? (
                        <div style={{ background: "rgba(52,211,153,0.04)" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 8, marginBottom: 10 }}>
                            {workoutFields.map(([key, label, type, options]) => (
                              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                <label style={labelStyle}>{label}</label>
                                {type === "select" ? (
                                  <select value={editForm[key]} onChange={event => setEditForm(form => ({ ...form, [key]: event.target.value }))} style={fieldStyleSm}>
                                    {options.map(option => <option key={option} value={option}>{option}</option>)}
                                  </select>
                                ) : (
                                  <input type={type} value={editForm[key]} onChange={event => setEditForm(form => ({ ...form, [key]: event.target.value }))} style={fieldStyleSm} />
                                )}
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 10 }}>
                            <label style={labelStyle}>Notes</label>
                            <input type="text" value={editForm.notes} onChange={event => setEditForm(form => ({ ...form, notes: event.target.value }))} placeholder="Optional notes" style={fieldStyleSm} />
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={saveEdit} style={{ ...btn, padding: "7px 16px", fontSize: 12, background: "rgba(52,211,153,0.2)", color: "#34d399", border: "1px solid rgba(52,211,153,0.4)" }}>Save</button>
                            <button onClick={() => setEditingId(null)} style={{ ...btn, padding: "7px 12px", fontSize: 12, background: "rgba(255,255,255,0.05)", color: "#777" }}>Cancel</button>
                            <button onClick={() => deleteWorkout(workout)} style={{ ...btn, padding: "7px 12px", fontSize: 12, background: "rgba(248,113,113,0.1)", color: "#f87171", border: "1px solid rgba(248,113,113,0.2)", marginLeft: "auto" }}>Delete</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 11, background: "rgba(52,211,153,0.1)", color: "#34d399", padding: "2px 8px", borderRadius: 6 }}>{workout.workoutType || "activity"}</span>
                              {isSyncedWorkout(workout) && <span style={{ fontSize: 10, color: "#fb923c", border: "1px solid rgba(251,146,60,0.24)", borderRadius: 6, padding: "2px 7px" }}>Synced</span>}
                              {timeRange(workout) && <span style={{ fontSize: 11, color: "#64748b" }}>{timeRange(workout)}</span>}
                            </div>
                            {isSyncedWorkout(workout) ? (
                              <button onClick={() => deleteWorkout(workout)} style={{ ...btn, padding: "3px 10px", fontSize: 11, background: "rgba(248,113,113,0.1)", color: "#f87171", border: "1px solid rgba(248,113,113,0.2)" }}>Delete</button>
                            ) : (
                              <button onClick={() => startEdit(workout)} style={{ ...btn, padding: "3px 10px", fontSize: 11, background: "rgba(52,211,153,0.08)", color: "#34d399", border: "1px solid rgba(52,211,153,0.2)" }}>Edit</button>
                            )}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
                            {number(workout.caloriesBurned) > 0 && <span style={{ fontSize: 11, color: "#fb923c" }}>{workout.caloriesBurned}kcal</span>}
                            {number(workout.heartRateAvg) > 0 && <span style={{ fontSize: 11, color: "#f87171" }}>{workout.heartRateAvg}bpm avg</span>}
                            {number(workout.heartRateMax) > 0 && <span style={{ fontSize: 11, color: "#fca5a5" }}>{workout.heartRateMax}bpm max</span>}
                            {number(workout.activeMinutes) > 0 && <span style={{ fontSize: 11, color: "#818cf8" }}>{workout.activeMinutes}min active</span>}
                            {number(workout.distance) > 0 && <span style={{ fontSize: 11, color: "#34d399" }}>{workout.distance}km</span>}
                            {number(workout.steps) > 0 && <span style={{ fontSize: 11, color: "#22d3ee" }}>{number(workout.steps).toLocaleString()} steps</span>}
                          </div>
                          {workout.notes && <div style={{ fontSize: 11, color: "#6ec89a", fontStyle: "italic", marginTop: 7 }}>{workout.notes}</div>}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
